// Bridge45 — locked domain constants.
// These encode the sprint strategy. They are deliberately not user-editable
// except where the spec says the founder may edit (runway inputs, records).

/** Structural sprint shape. Calendar dates come from Sprint Initialization (T0). */
export const SPRINT = {
  totalDays: 45,
};

/** Gate checkpoints as day offsets from Day 1. Dates are derived at runtime. */
export const GATES = [
  { day: 15, key: 'g15' },
  { day: 30, key: 'g30' },
  { day: 45, key: 'g45' },
];

export const EVIDENCE_LEVELS = [
  {
    id: 'E0',
    name: 'Hypothesis',
    line: 'We think they might care.',
    note: 'No traction credit.',
    weight: 0,
    tone: 'neutral',
  },
  {
    id: 'E1',
    name: 'Engagement',
    line: 'Reply, introduction, meeting booked.',
    note: 'Leading indicator only. This is not traction.',
    weight: 1,
    tone: 'neutral',
  },
  {
    id: 'E2',
    name: 'Problem Confirmed',
    line: 'Meeting occurred and the counterparty explicitly confirmed a relevant problem or use case.',
    note: 'Real, but not yet commercial.',
    weight: 2,
    tone: 'watch',
  },
  {
    id: 'E3',
    name: 'Commitment of Time / Information',
    line: 'The counterparty commits information, time, or resources: a named asset, documents, data-room access, a second meeting, or technical/legal involvement.',
    note: 'They are spending their own resources on you.',
    weight: 3,
    tone: 'watch',
  },
  {
    id: 'E4',
    name: 'Commercial / Execution Commitment',
    line: 'Budget, proposal requested, paid POC under approval, mandate negotiation, partner architecture accepted, regulator provides a formal next gate.',
    note: 'This is meaningful traction.',
    weight: 4,
    tone: 'good',
  },
  {
    id: 'E5',
    name: 'Binding Proof',
    line: 'Paid, signed, contract, mandate, executable regulated partner commitment.',
    note: 'The strongest proof.',
    weight: 5,
    tone: 'good',
  },
];

// Recorded work that is explicitly NOT evidence.
export const ACTIVITY = {
  id: 'ACT',
  name: 'Activity',
  line: 'Motion. Research, sending, preparing, building, posting.',
  note: 'Necessary sometimes. Never proof.',
  weight: -1,
  tone: 'flat',
};

export const EVIDENCE_BY_ID = Object.fromEntries(
  [ACTIVITY, ...EVIDENCE_LEVELS].map((e) => [e.id, e])
);

export const MARKETS = ['Dubai', 'Saudi', 'Infrastructure', 'Fundraising']; // test/offline fixture only — live UI uses planDefinition.markets


export const MARKET_STATUS = {
  Dubai: { status: 'ACTIVE', allocation: 55, role: 'First transaction market' },
  Saudi: { status: 'VALIDATION', allocation: 20, role: 'First scale-validation market' },
  Infrastructure: { status: 'COMMERCIALIZE NOW', allocation: 10, role: 'Cash engine' },
  Fundraising: { status: 'ACTIVE', allocation: 10, role: 'Runway + structural leadership gap' },
  Other: { status: 'PARKED', allocation: 5, role: 'Qatar, Bahrain, administration' },
};

export const TIME_TARGETS = [
  { key: 'dubai', label: 'Dubai', target: 55 },
  { key: 'saudi', label: 'Saudi', target: 20 },
  { key: 'infrastructure', label: 'Infrastructure', target: 10 },
  { key: 'fundraising', label: 'Fundraising / co-founder', target: 10 },
  { key: 'other', label: 'Other / Qatar / Bahrain / admin', target: 5 },
];

export const D001_STAGES = [
  'Target',
  'Contacted',
  'Discovery',
  'Asset Screen',
  'Readiness Proposed',
  'Readiness Active',
  'Execution Structuring',
  'Mandate / Implementation',
  'Execution Ready',
  'Live',
  'Won / Lost / Parked',
];

/** Offline / test fixture — live UI uses orgOpportunityStageLabels(state). */
export const OFFLINE_OPP_STAGES = [
  'Observed',
  'Qualified',
  'Asset Identified',
  'Asset Screen',
  'D001 Candidate',
  'Primary D001',
  'Readiness Proposed',
  'Readiness Active',
  'Execution Structuring',
  'Mandate / Implementation',
  'Execution Ready',
  'Live',
  'Won / Lost / Parked',
];

/** Offline consulting fixture — live UI uses plan pipeline stages. */
export const OFFLINE_CONSULTING_OPP_STAGES = [
  'Observed',
  'Qualified',
  'Contacted',
  'Engaged',
  'Discovery',
  'Proposal',
  'Sprint SOW',
  'Architecture upsell',
  'Won / Lost / Parked',
];

// Evidence required to LEAVE each stage. No arbitrary progression.
export const D001_STAGE_EXIT = {
  Target: { minLevel: 'E1', text: 'A named sponsor has replied or a meeting is booked.' },
  Contacted: { minLevel: 'E2', text: 'Meeting occurred and the sponsor confirmed a relevant problem.' },
  Discovery: { minLevel: 'E3', text: 'A named asset exists and the sponsor has shared information about it.' },
  'Asset Screen': { minLevel: 'E3', text: 'Ownership/title information supplied and the asset passes initial screening.' },
  'Readiness Proposed': { minLevel: 'E4', text: 'Sponsor has requested or priced a readiness proposal (budget acknowledged).' },
  'Readiness Active': { minLevel: 'E4', text: 'Readiness work is commissioned and underway.' },
  'Execution Structuring': { minLevel: 'E4', text: 'A licensed partner has accepted the transaction architecture.' },
  'Mandate / Implementation': { minLevel: 'E5', text: 'Signed mandate or executed implementation agreement.' },
  'Execution Ready': { minLevel: 'E5', text: 'Regulated pathway is executable and capital acceptance is confirmed.' },
  Live: { minLevel: 'E5', text: 'Transaction is live.' },
  'Won / Lost / Parked': { minLevel: 'E0', text: 'Terminal stage. Record the reason in the Decision Log.' },
};

export const REGULATED_ACTIVITIES = [
  { key: 'issuance', label: 'Token issuance', critical: true },
  { key: 'arranging', label: 'Arranging', critical: true },
  { key: 'marketing', label: 'Marketing', critical: true },
  { key: 'orders', label: 'Investor orders', critical: true },
  { key: 'kyc', label: 'KYC / AML', critical: true },
  { key: 'money', label: 'Investor money', critical: true },
  { key: 'custody', label: 'Custody', critical: true },
  { key: 'transfer', label: 'Transfer', critical: true },
  { key: 'secondary', label: 'Secondary exchange', critical: false },
  { key: 'dld', label: 'DLD / title', critical: true },
  { key: 'servicing', label: 'Investor servicing', critical: false },
  { key: 'distributions', label: 'Distributions', critical: true },
];

export const RESPONSIBLE_PARTIES = ['Blocks', 'Partner', 'Unknown', 'Not Applicable'];

export const PARTNER_STAGES = [
  'Identified',
  'Qualified',
  'Architecture Review',
  'Commercial Review',
  'Legal / Compliance',
  'Pilot Ready',
  'Active',
  'Blocked / Rejected',
];

export const INFRA_STAGES = [
  'Target',
  'Contacted',
  'Discovery',
  'Pain Confirmed',
  'Technical Discovery',
  'POC Proposed',
  'Commercial Review',
  'Contract / POC',
  'Deployment',
  'Expansion',
  'Lost / Parked',
];

export const INFRA_CASH_STAGE_WEIGHT = {
  Target: 0,
  Contacted: 1,
  Discovery: 2,
  'Pain Confirmed': 3,
  'Technical Discovery': 4,
  'POC Proposed': 5,
  'Commercial Review': 6,
  'Contract / POC': 7,
  Deployment: 8,
  Expansion: 9,
  'Lost / Parked': -1,
};

export const MODULES = {
  Core: [
    'Sponsor / issuer portal',
    'Asset setup',
    'Investor portal',
    'Administration',
    'Reporting',
    'White label',
  ],
  Optional: [
    'Tokenization',
    'KYC integration',
    'Wallet / custody',
    'Payments',
    'Marketplace / transfers',
    'APIs',
    'CRM / investor orchestration',
  ],
};

export const FEATURE_CLASSES = ['EXISTS', 'CONFIGURE', 'INTEGRATE', 'CUSTOM', 'FROZEN'];

export const PRODUCT_CLASSES = ['KEEP', 'D001', 'CUSTOMER', 'PARTNER', 'FROZEN'];

export const EXPENSE_CATEGORIES = [
  { key: 'KEEP', label: 'KEEP', desc: 'Company / platform survival.' },
  { key: 'PROVE', label: 'PROVE', desc: 'Creates commercial or regulatory evidence.' },
  { key: 'EXTEND', label: 'EXTEND', desc: 'Extends runway or financing.' },
  { key: 'STOP', label: 'STOP', desc: 'Everything else.' },
];

export const SIGNAL_STATES = ['none', 'weak', 'strong'];
export const SIGNAL_LABELS = { none: 'No evidence', weak: 'Weak signal', strong: 'Strong signal' };

export const SAUDI_SIGNALS = [
  { key: 'commercial', label: 'COMMERCIAL', question: 'Has someone identified budget?' },
  { key: 'useCase', label: 'USE CASE', question: 'Is there a real developer / Contribution / enterprise use case?' },
  { key: 'regulatedPartner', label: 'REGULATED PARTNER', question: 'Is a CMA / regulated institution seriously engaged?' },
  { key: 'government', label: 'GOVERNMENT', question: 'Is there a concrete REGA / PropTech next gate?' },
];

export const SAUDI_PIPELINE_FIELDS = [
  { key: 'targets', label: 'Targets' },
  { key: 'meetings', label: 'Meetings' },
  { key: 'painConfirmed', label: 'Pain confirmed' },
  { key: 'technicalDiscoveries', label: 'Technical discoveries' },
  { key: 'pocs', label: 'POCs' },
  { key: 'proposals', label: 'Proposals' },
  { key: 'budget', label: 'Budget identified' },
  { key: 'paid', label: 'Paid' },
];

export const EXCEPTION_TRIGGERS = [
  'Regulatory invalidation',
  'Material inbound pull',
  'Runway event',
  'Exceptional commercial opportunity',
  'Material regulatory change',
  'Wrong-tenant / contaminated strategy template',
  'Plan recompile',
];

export const DECISION_TYPES = [
  'Strategy',
  'Sprint Initialization',
  'Sprint re-baseline',
  'Sprint closeout',
  'D001 selection',
  'Pricing',
  'Asset',
  'Partner',
  'Product',
  'Spend',
  'Market',
  'Fundraising',
];

// Offline/test Blocks Estate epic catalog only. Live orgs use planDefinition from the API.
export const EPICS = [
  // Fallback catalog for offline/web tests. Live orgs use planDefinition from the API.
  { id: 'E01', title: 'Strategy Lock & Governance' },
  { id: 'E02', title: 'Runway & Burn Control' },
  { id: 'E03', title: 'Nexus Founder Mode' },
  { id: 'E04', title: 'Dubai D001 Asset & Sponsor' },
  { id: 'E05', title: 'Dubai Regulated Execution' },
  { id: 'E06', title: 'Dubai Capital & Distribution' },
  { id: 'E07', title: 'Blocks Infrastructure' },
  { id: 'E08', title: 'Saudi Scale Validation' },
  { id: 'E09', title: 'Qatar & Bahrain Optionality' },
  { id: 'E10', title: 'AI Commercial Workforce' },
  { id: 'E11', title: 'Compliance & Stealth GTM' },
  { id: 'E12', title: 'Fundraising & Co-Founder' },
  { id: 'E13', title: 'Cadence, Gates & Decision' },
];

/** Delivery epics produce gate evidence. Continuous epics are governed, never % complete. */
export const EPIC_CLASS = {
  E01: 'continuous',
  E02: 'continuous',
  E03: 'continuous',
  E04: 'delivery',
  E05: 'delivery',
  E06: 'delivery',
  E07: 'delivery',
  E08: 'delivery',
  E09: 'parked',
  E10: 'parked',
  E11: 'continuous',
  E12: 'delivery',
  E13: 'continuous',
};

export const EPIC_STRATEGY_STATUS = {
  E01: 'Support',
  E02: 'Support',
  E03: 'Support',
  E04: 'Active',
  E05: 'Active',
  E06: 'Active',
  E07: 'Support',
  E08: 'Validation',
  E09: 'Parked',
  E10: 'Parked',
  E11: 'Support',
  E12: 'Support',
  E13: 'Support',
};

export const EPIC_MARKET = {
  E04: 'Dubai',
  E05: 'Dubai',
  E06: 'Dubai',
  E07: 'Infrastructure',
  E08: 'Saudi',
  E12: 'Fundraising',
};

export const ACTION_WORKSTREAMS = ['Dubai', 'D001', 'Saudi', 'Infrastructure', 'Fundraising', 'Runway', 'Other'];
export const ACTION_PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];
export const MOVE_STATUSES = ['Backlog', 'Ready', 'Today', 'In progress', 'Closed'];
export const ACTION_STATUSES = [
  ...MOVE_STATUSES,
  'Open',
  'In Progress',
  'Blocked',
  'Completed',
  'Parked',
  'Cancelled',
  'Superseded',
];
export const ACTION_OPEN_STATUSES = ['Backlog', 'Ready', 'Today', 'In progress', 'Open', 'In Progress', 'Blocked'];
export const ACTION_CLOSED_STATUSES = ['Closed', 'Completed', 'Parked', 'Cancelled', 'Superseded'];
export const MOVE_DISPOSITIONS = [
  { value: 'evidence', label: 'Evidence gained' },
  { value: 'commercial', label: 'Commercial object updated' },
  { value: 'learning', label: 'Learning only' },
  { value: 'stopped', label: 'Stopped' },
];
export const MOVE_SOURCES = ['Manual', 'Rule', 'BridgeAI'];
export const ACTION_GATES = [
  { value: 'none', label: 'None' },
  { value: '15', label: 'Day 15' },
  { value: '30', label: 'Day 30' },
  { value: '45', label: 'Day 45' },
];
export const ACTION_LINK_KINDS = [
  { value: '', label: 'None' },
  { value: 'account', label: 'Account' },
  { value: 'opportunity', label: 'Opportunity' },
  { value: 'asset', label: 'Asset' },
  { value: 'partner', label: 'Partner' },
  { value: 'blocker', label: 'Blocker' },
];
export const ACTION_RESULTS = [
  { value: '', label: 'Not graded' },
  { value: 'yes', label: 'Expected evidence created' },
  { value: 'partial', label: 'Partial evidence' },
  { value: 'no', label: 'No evidence created' },
  { value: 'operational', label: 'Operational outcome only' },
];
export const EXPECTED_EVIDENCE_OPTIONS = [
  { value: 'E0', label: 'E0 — Hypothesis' },
  { value: 'E1', label: 'E1 — Engagement' },
  { value: 'E2', label: 'E2 — Problem Confirmed' },
  { value: 'E3', label: 'E3 — Commitment of Time / Information' },
  { value: 'E4', label: 'E4 — Commercial / Execution Commitment' },
  { value: 'E5', label: 'E5 — Binding Proof' },
  { value: 'OPS', label: 'Internal enabler—no evidence credit' },
];

/** Primary founder navigation — jobs, not internal data models. */
export const NAV = [
  { key: 'command', label: 'Today' },
  { key: 'pipeline', label: 'Pipeline' },
  { key: 'review', label: 'Review' },
  { key: 'strategy', label: 'Strategy' },
  { key: 'more', label: 'More' },
];

/** Escape hatches under the More nav dropdown. D001 is filtered by adaptive surfacing. */
export const MORE_NAV = [
  { key: 'actions', label: 'Execution Queue' },
  { key: 'plan-configurator', label: 'Plan Configurator' },
  { key: 'primary-deal', label: 'Primary-deal workspace', d001: true },
  { key: 'evidence', label: 'Evidence ledger' },
  { key: 'runway', label: 'Runway detail' },
  { key: 'gates', label: 'Decision gates' },
  { key: 'diagnostics', label: 'Diagnostics / Audit' },
  { key: 'settings', label: 'Settings' },
];

/** Offline/test Blocks Estate pipeline tabs. Live UI uses orgPipelineTabs(planDefinition). */
export const PIPELINE_TABS = [
  { key: 'dubai', label: 'Dubai' },
  { key: 'saudi', label: 'Saudi' },
  { key: 'infrastructure', label: 'Infrastructure' },
];

export const STRATEGY_TABS = [
  { key: 'plan', label: 'Strategy Map' },
  { key: 'decisions', label: 'Decisions & Exceptions' },
];

/** Offline/test Blocks Estate mandate. Live Bridge uses planDefinition.bridgeMandate. */
export const BRIDGE_MANDATE = `You are not here to help the founder do more. You are here to make the founder do fewer, higher-leverage things. Challenge activity that does not produce commercial evidence. Prefer E4/E5 movement, removal of the primary constraint, runway preservation, and achievement of the next gate over task completion or activity volume. Do not recommend new markets, speculative product work, social-media activity, or new workstreams merely because they are interesting. Keep the founder executing the shortest evidence-backed path through the locked 45-day strategy. Do not invent facts that are not in the briefing. Recommend at most three founder actions. Never silently change Dubai = Active, Saudi = Validation, Qatar/Bahrain = Parked, Blocks Infrastructure = Cash Engine, the 45-day strategy, or gate definitions — those require a Proposed Strategy Exception.`;

export const BRIDGE_ASK_QUESTIONS = [
  'Why is this my #1 priority?',
  'Which D001 candidate is strongest?',
  'What must happen to pass the next gate?',
  'Am I spending too much time on Saudi?',
  'What should I stop doing?',
  'If the Primary dies, what is our next best path?',
];

export const EMPTY_COPY = {
  dubai: 'D001 does not exist yet. The immediate objective is one credible sponsor + one suitable asset.',
  noPrimary:
    'No Primary D001 yet. Build enough opportunity density to choose the strongest first transaction rather than attaching to the first interested account.',
  noCandidates:
    'No D001 candidate yet. Focus on converting discovery into qualified opportunities and real asset information.',
  thinPipeline:
    'The first-deal engine is under-supplied. Increase high-quality target activity before adding execution complexity.',
  noAccounts:
    'No accounts recorded. Add names by hand or import CSV — Nexus sync is optional for consulting tenants.',
  queue: 'Nothing in the founder queue. Either no account has earned Tier A, or Tier A accounts have no next action.',
  saudi: 'Saudi has not earned more resources yet. Create evidence before expanding the commitment.',
  infrastructure:
    'No B2B cash path has been proven yet. Find the buyer who would rather buy Blocks than rebuild it.',
  evidence: 'Activity exists. Proof does not — yet.',
  partners: 'No regulated execution partner identified. Without one, D001 cannot be executed legally.',
  expenses: 'No spend recorded. Every material expense should name the evidence it is buying.',
  decisions: 'No decisions recorded. This log is the institutional memory of the sprint.',
  reviews: 'No week has been closed yet. Friday review is how the sprint stays honest.',
  top3: 'No AI plan for today yet. Run a re-study after commercial state exists — do not fill Today from a to-do list.',
  actions: 'No executable commercial moves in the queue. The Bridge generates them from the locked plan and commercial state.',
  history: 'No closed moves yet. Results and evidence land here when a move is finished.',
  closeout: 'No day closed yet. Two minutes at the end of the day.',
  sprintReviews: 'No sprint post-mortem yet. Close Day 45 — or complete an early cycle-close before re-baselining.',
};
